"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import useMultidietStore from "@/features/multidiet/store/multidiet-store";
import type { MultidietParamsType } from "@/features/multidiet/types/multidiet.type";
import { useMultidietSetup } from "@/features/multidiet-picker/api/hooks/use-multidiet-setup";
import useMultidietSetupStore from "@/features/multidiet-picker/store/multidiet-picker.store";
import type { MultidietPickerFormType } from "@/features/multidiet-picker/types/multidiet-picker-form.type";
import { getLocaleForPathname, getPathname } from "@/i18n/navigation";
import { useMobile } from "@/lib/hooks/use-mobile";
import { objectToQueryString } from "@/lib/utilities";

const DEFAULT_CALORIES = "1800";

const getClosestCalories = ({
  availableCalories,
  targetCalories,
}: {
  availableCalories: string[];
  targetCalories: string;
}) => {
  const parsedTargetCalories = Number(targetCalories);

  if (Number.isNaN(parsedTargetCalories)) {
    return DEFAULT_CALORIES;
  }

  const normalizedAvailableCalories = availableCalories.filter(
    (calorie) => !Number.isNaN(Number(calorie)),
  );

  if (normalizedAvailableCalories.length === 0) {
    return targetCalories;
  }

  return normalizedAvailableCalories.reduce(
    (closestCalories, currentCalories) =>
      Math.abs(Number(currentCalories) - parsedTargetCalories) <
      Math.abs(Number(closestCalories) - parsedTargetCalories)
        ? currentCalories
        : closestCalories,
  );
};

const multidietPickerFormSchema = z.object({
  dietTypeMobile: z.string(),
  dietTypeAny: z.string(),
  dietType: z.array(z.string()),
  calories: z.string(),
});

const getInitialDietTypeAny = (tagsFromUrl: string[]) => {
  if (tagsFromUrl.includes("ANY") || tagsFromUrl.length > 1) {
    return "ANY";
  }

  return "";
};

const getInitialDietType = (tagsFromUrl: string[]) => {
  if (tagsFromUrl.length === 0) {
    return ["CUSTOM_CHOICE"];
  }

  if (tagsFromUrl.includes("ANY") || tagsFromUrl.length > 1) {
    return [];
  }

  return tagsFromUrl;
};

const getInitialDietTypeMobile = (tagsFromUrl: string[]) => {
  if (tagsFromUrl.length === 0) {
    return "CUSTOM_CHOICE";
  }

  if (tagsFromUrl.includes("ANY") || tagsFromUrl.length > 1) {
    return "ANY";
  }

  return tagsFromUrl[0] || "CUSTOM_CHOICE";
};

export const useMultidietPickerForm = () => {
  const locale = getLocaleForPathname();
  const currentPathname = usePathname();
  const pathname = getPathname({ href: "/multidiet-configuration", locale });
  const searchParams = useSearchParams();
  const [previousDietTypeMobile, setPreviousDietTypeMobile] =
    useState<string>("");
  const {
    setIsFetching,
    setMultidietSetup,
    setMultidietSetupLabels,
    setFirstDeliveryDate,
    setMultidietSelectedLabel,
    multidietSetup,
  } = useMultidietSetupStore();
  const { deliveryDate } = useMultidietStore();

  const { isMobileExtendedPlus } = useMobile();

  const tagsFromUrl = useMemo(() => {
    const tags = searchParams.get("tags") || searchParams.get("tagsSlugs");
    if (tags?.includes("CUSTOM_CHOICE")) {
      return ["CUSTOM_CHOICE"];
    }
    return tags ? tags.split(",") : [];
  }, [searchParams]);

  const rawCaloriesFromUrl = useMemo(() => {
    const calories =
      searchParams.get("calories") || searchParams.get("caloricity");

    if (!calories || Number.isNaN(Number(calories))) {
      return DEFAULT_CALORIES;
    }

    return calories;
  }, [searchParams]);

  const caloriesFromUrl = useMemo(() => {
    if (!multidietSetup?.calories) {
      return rawCaloriesFromUrl;
    }

    return getClosestCalories({
      availableCalories: multidietSetup.calories,
      targetCalories: rawCaloriesFromUrl,
    });
  }, [multidietSetup?.calories, rawCaloriesFromUrl]);

  const form = useForm<MultidietPickerFormType>({
    resolver: zodResolver(multidietPickerFormSchema),
    defaultValues: {
      dietTypeMobile: "",
      dietTypeAny: getInitialDietTypeAny(tagsFromUrl),
      dietType: getInitialDietType(tagsFromUrl),
      calories: caloriesFromUrl,
    },
  });

  const {
    data: multidietSetupResponse,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
  } = useMultidietSetup({
    queryParams: {
      tags: isMobileExtendedPlus
        ? form.getValues("dietTypeMobile") || ""
        : form.getValues("dietType").join(","),
      calories: form.getValues("calories"),
    },
    options: {
      staleTime: 0,
    },
  });

  const dietTypeWatcher = useWatch({
    control: form.control,
    name: "dietType",
  });

  const dietTypeMobileWatcher = useWatch({
    control: form.control,
    name: "dietTypeMobile",
  });

  const caloriesWatcher = useWatch({
    control: form.control,
    name: "calories",
  });

  const dietTypeAnyWatcher = useWatch({
    control: form.control,
    name: "dietTypeAny",
  });

  useEffect(() => {
    if (isMobileExtendedPlus) {
      form.setValue("dietTypeMobile", getInitialDietTypeMobile(tagsFromUrl));
    }
  }, [form, isMobileExtendedPlus, tagsFromUrl]);

  useEffect(() => {
    if (form.getValues("calories") === caloriesFromUrl) {
      return;
    }

    form.setValue("calories", caloriesFromUrl);
  }, [caloriesFromUrl, form]);

  useEffect(() => {
    if (!isMobileExtendedPlus) {
      if (dietTypeAnyWatcher === "ANY" && dietTypeWatcher.length !== 0) {
        form.setValue("dietTypeAny", "");
      }

      if (dietTypeAnyWatcher !== "ANY" && dietTypeWatcher.length === 0) {
        form.setValue("dietTypeAny", "ANY");
      }

      if (!(dietTypeWatcher.length === 0 && dietTypeAnyWatcher === "ANY")) {
        refetch();
      }

      // decision to allow only one diet type to be selected at the start, not changing the form's operating principle
      if (dietTypeWatcher.length > 1) {
        form.setValue("dietType", dietTypeWatcher.slice(1));
      }
    }
  }, [dietTypeWatcher, dietTypeAnyWatcher, isMobileExtendedPlus]);

  useEffect(() => {
    setIsFetching(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (isSuccess) {
      setMultidietSetup(multidietSetupResponse.setup);
      setMultidietSetupLabels(multidietSetupResponse.labels);
      setFirstDeliveryDate(new Date(multidietSetupResponse.firstDeliveryDate));
    }
  }, [isSuccess, multidietSetupResponse]);

  const hasAnySelected = (diet: string) => diet === "ANY";

  const firstNonAnyDiet = multidietSetup?.dietTypes.find(
    (type) => type.dietTag !== "ANY",
  );

  useEffect(() => {
    // For single select: if previously was ANY and now empty -> choose first non-ANY
    if (hasAnySelected(previousDietTypeMobile)) {
      if (!dietTypeMobileWatcher || dietTypeMobileWatcher === "") {
        form.setValue("dietTypeMobile", firstNonAnyDiet?.dietTag || "");
        setPreviousDietTypeMobile(dietTypeMobileWatcher || "");
        return;
      }
    }

    if (hasAnySelected(dietTypeMobileWatcher)) {
      if (!hasAnySelected(previousDietTypeMobile) && previousDietTypeMobile) {
        form.setValue("dietTypeMobile", "ANY");
        setPreviousDietTypeMobile(dietTypeMobileWatcher);
        return;
      }
    }

    if (previousDietTypeMobile && !dietTypeMobileWatcher) {
      form.setValue("dietTypeMobile", "ANY");
      setPreviousDietTypeMobile(dietTypeMobileWatcher || "");
      return;
    }

    refetch();

    setPreviousDietTypeMobile(dietTypeMobileWatcher || "");
  }, [dietTypeMobileWatcher]);

  const onSubmit = (
    data: {
      dietType: string[];
      dietTypeMobile: string;
      dietTypeAny: string;
      calories: string;
    },
    event?: React.BaseSyntheticEvent,
  ) => {
    event?.preventDefault();

    const nativeEvent = event?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
    const variantJson = submitter?.dataset?.dietvariant;
    const variant = variantJson ? JSON.parse(variantJson) : null;
    setMultidietSelectedLabel(variant);
    const parsedData: MultidietParamsType = {
      tags: data.dietType.join(","),
      calories: Number(data.calories),
      type: variant?.type || "BASIC",
    };

    if (data.dietTypeMobile) {
      parsedData.tags = data.dietTypeMobile;
    } else if (data.dietType.length === 0 && data.dietTypeAny === "ANY") {
      parsedData.tags = "ANY";
    }

    if (deliveryDate && currentPathname.includes(pathname)) {
      parsedData.dates = deliveryDate
        .map((date) => format(date, "yyyy-MM-dd"))
        .join(",");
    }
    setIsFetching(false);

    window.location.href = `${pathname}${objectToQueryString(parsedData)}`;
  };

  return { isLoading, form, dietTypeWatcher, caloriesWatcher, onSubmit };
};
